// "When fastening fails: escalating to hardware" — the unified connection
// sheet, the 2-step capacity/demand workflow, the auto fastener schedule, the
// failing verdict, and the two-sheet escalation record. The point of the
// design is stated plainly: a failed nail check is preserved evidence, not an
// error to delete. Fixture numbers import from calcPreviewFixtures (shared
// with the figure captures).
import type { HelpArticle } from "../../types";
import {
  AUTO_SCHEDULE_DEMO,
  CN1,
  CN2,
  LUSI26_CAPACITY,
} from "../../../calculations/calcPreviewFixtures";

const grp = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const pct = (u: number): string => `${Math.round(u * 100)}%`;

export const fasteningEscalationArticle: HelpArticle = {
  id: "fastening-escalation",
  title: "When fastening fails: escalating to hardware",
  description:
    "The unified connection sheet's escalation ladder: a failing nail schedule, the Check hardware action, and the two-sheet record where the failed check prints as evidence.",
  tags: ["connection", "hardware", "escalation", "nails", "fastener schedule", "connector"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: `The nail check that fails is the reason the hardware gets specified, and the record keeps that failure. This article follows ${CN2.mark}, a toe-nailed rafter connection failing at ${pct(CN2.maxDcr)}, through the escalation that ends with a published hanger and two printed sheets: the failed check, and the product that supersedes it.`,
    },

    { kind: "heading", level: 2, text: "One connection sheet, five fastenings", id: "unified-sheet" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", CN2.mark] },
    {
      kind: "paragraph",
      text: "The catalog has one Connection card, and it opens as **Nails** — the escalation ladder starts minimal. The header's **Fastening** master control swaps the sheet among five models: Nails, Screws, Lags, Bolts, and Hardware (catalog product). The first four compute capacity from the NDS yield-limit equations; the fifth validates an exact published product row.",
    },
    {
      kind: "paragraph",
      text: "Switching fastenings retargets the calculator but keeps the sheet. The sheet id survives, so a linked reaction survives with it. Groups that describe the **connection** — members, conditions, loads, uplift — carry across wherever the target schema shares the key; groups that describe the **fastener** re-seed from the target's registry defaults, so a nail's bending yield never leaks into a lag calculation.",
    },
    {
      kind: "shot",
      shotId: "fastening-master-control",
      caption: `${CN2.mark}'s header: the Fastening master control, the check chips, and a verdict that is about to matter.`,
    },

    { kind: "heading", level: 2, text: "The 2-step workflow", id: "two-step" },
    {
      kind: "paragraph",
      text: "Every fastening sheet runs the same two declarations. **Step 1 — capacity**: on a dowel sheet, the entered schedule framed as a computed capacity (never a product row); on a hardware sheet, one exact published capacity row. **Step 2 — demand**: manual per-type fields, or one linked support reaction that the engine reads live at every recalculation.",
    },
    { kind: "registry-group", calculatorId: "wood_nail_connection_asd", groupId: "loads" },
    {
      kind: "paragraph",
      text: "Linking supersedes typing. While a reaction link is active the manual demand fields stay persisted but disabled, under one sentence: \"Manual demand values are retained but ignored while the reaction link is active — the linked packet is the demand authority.\" Unlink and the typed values come back untouched.",
    },
    { kind: "registry-field", calculatorId: "connector_product_capacity", fieldKey: "manual_design_demand_lb" },
    {
      kind: "shot",
      shotId: "connection-two-step",
      caption: "The connection check setup: capacity declared above, demand source below, state on the right.",
    },

    { kind: "heading", level: 2, text: "The auto fastener schedule", id: "auto-schedule" },
    {
      kind: "paragraph",
      text: "On nail sheets, **Auto schedule** hands the count to the engine: it derives the maximum viable fastener count from the declared contact geometry — holding-member width, contact length, side-member width — and supersedes the entered count and the toe-nail/end-grain toggles. The derived schedule prints with its governing constraint, so the record says which spacing rule capped the count.",
    },
    { kind: "registry-group", calculatorId: "wood_nail_connection_asd", groupId: "schedule" },
    { kind: "registry-field", calculatorId: "wood_nail_connection_asd", fieldKey: "auto_schedule" },
    {
      kind: "values",
      caption: `The derived schedule for the default rafter-to-plate geometry, 16d toe-nails (D = ${AUTO_SCHEDULE_DEMO.nailDiameterIn} in).`,
      rows: [
        { label: "Contact geometry", value: `holding ${AUTO_SCHEDULE_DEMO.holdingWidthIn} in · contact ${AUTO_SCHEDULE_DEMO.contactLengthIn} in · side ${AUTO_SCHEDULE_DEMO.sideWidthIn} in` },
        { label: "Spacing at D = 0.162 in", value: `end ${AUTO_SCHEDULE_DEMO.distancesIn.end} in · edge ${AUTO_SCHEDULE_DEMO.distancesIn.edge} in · in-row ${AUTO_SCHEDULE_DEMO.distancesIn.inRow} in · rows ${AUTO_SCHEDULE_DEMO.distancesIn.rowSpacing} in` },
        { label: "Result", value: `${AUTO_SCHEDULE_DEMO.nFasteners} max viable, rows of ${AUTO_SCHEDULE_DEMO.perRow.join(" + ")}` },
        { label: "Engine summary line", value: AUTO_SCHEDULE_DEMO.summary },
      ],
    },
    {
      kind: "shot",
      shotId: "connection-auto-schedule",
      caption: "Step 1 with Auto schedule on: the engine's derived count and governing constraint, not a typed guess.",
    },

    { kind: "heading", level: 2, text: "The failing verdict", id: "failing-verdict" },
    {
      kind: "paragraph",
      text: `${CN2.mark} checks ${CN2.schedule} against its linked rafter reaction and fails: **${CN2.governingLabel} at ${pct(CN2.maxDcr)}**, status \`Fail · ${pct(CN2.maxDcr)}\`. The record keeps the whole trace — the yield-mode table (mode IV governs at Z = 140.7 lb per fastener), the factor ledger with CD 1.6 for the wind-governed combination, and the per-combination capacity tables. Its sibling ${CN1.mark} passes the same kind of check at ${pct(CN1.maxDcr)} with ${CN1.schedule}; the difference between them is the whole argument for hardware.`,
    },
    {
      kind: "paragraph",
      text: "A failing dowel check raises the escalation banner in the result pane: a `FAIL` tag, the fastening named insufficient (\"Nails insufficient — check published hardware\"), the governing check and percentage, and exactly what the action will create.",
    },
    {
      kind: "shot",
      shotId: "connection-escalation-banner",
      caption: `The escalation banner on ${CN2.mark}: the verdict, the consequence, and the one action.`,
    },

    { kind: "heading", level: 2, text: `Check hardware (${CN2.mark}H)`, id: "check-hardware" },
    {
      kind: "paragraph",
      text: `**Check hardware (${CN2.mark}H)** creates the second sheet: a hardware capacity check marked \`${CN2.mark}H\`, carrying the design method and LRFD live-load duration across, and landing you directly in the product picker. The reaction link is recreated on ${CN2.mark}H the moment an exact capacity row is selected — not before, because the api honestly refuses a connection link until a capacity row exists to price it.`,
    },
    {
      kind: "paragraph",
      text: `Both sheets receive an escalation note that renders on screen and folds into the printed assumptions on recompute, so it appears on both records and in both exports. On ${CN2.mark}: "Fastening schedule ⟨schedule⟩ is insufficient (⟨check⟩ at ⟨utilization⟩); superseded by hardware check ${CN2.mark}H per project escalation policy." On ${CN2.mark}H: "Escalated from ${CN2.mark}: fastening schedule ⟨schedule⟩ was insufficient (⟨check⟩ at ⟨utilization⟩); this hardware check supersedes it."`,
    },
    {
      kind: "paragraph",
      text: `The picker shows **published capacity rows** — capacity first, then everything an auditor asks next. Each row carries the product and exact installation, the capacity with its basis chip (design method and CD), the load direction, the wood condition, and the source document and table. Only rows published for the sheet's design method appear, and the server re-resolves the row at every calculation; the picker's own caption says so. **Use capacity** seats the row and the check runs.`,
    },
    {
      kind: "values",
      caption: `The row that closes ${CN2.mark}H in the demo.`,
      rows: [
        { label: "Product", value: LUSI26_CAPACITY.name },
        { label: "Capacity", value: `${grp(LUSI26_CAPACITY.capacityLb)} lb · ${LUSI26_CAPACITY.direction} · ${LUSI26_CAPACITY.designMethod} · CD ${LUSI26_CAPACITY.loadDurationCd}` },
        { label: "Installation", value: `${LUSI26_CAPACITY.installationLabel} · ${LUSI26_CAPACITY.woodSpeciesGroup}` },
        { label: "Fastening", value: LUSI26_CAPACITY.fastenerSchedule },
        { label: "Source", value: `${LUSI26_CAPACITY.sourceDocument} · ${LUSI26_CAPACITY.sourceTable}` },
      ],
    },
    {
      kind: "shot",
      shotId: "hardware-capacity-rows",
      caption: "Capacity-first rows in the hardware picker: the number, its basis, its installation, its source.",
    },

    { kind: "heading", level: 2, text: "Two sheets, one record", id: "two-sheet-record" },
    {
      kind: "paragraph",
      text: `The register now shows ${CN2.mark} failing at ${pct(CN2.maxDcr)} and ${CN2.mark}H carrying the hanger, and both print. That is the point. The failed nail check is the evidence that hardware was required; the note on each sheet points at the other; a reviewer reads the decision instead of reconstructing it. Deleting the failure would delete the reasoning.`,
    },
    {
      kind: "callout",
      variant: "tip",
      title: "If you switch fastenings instead",
      body: "The escalation banner is for the hardware jump. Trying screws or lags first is one change of the Fastening master control on the same sheet — the demand link and the connection groups ride along, and only the fastener model re-seeds.",
    },

    { kind: "related", topics: ["linking-reactions", "your-first-member"] },
  ],
};
